import React, { useEffect } from "react";
import { useQuery } from "@apollo/react-hooks";

// script to ensure focus visible with keyboard not with mouse:
import * as focusVisible from "focus-visible";

//components
import PageMeta from "core/PageMeta";
import NotFound from "core/NotFound";
import IconDetailLayout from "icons/IconDetailLayout";
import IconDetailSteps from "icons/IconDetailSteps";
import Loading from "./Loading";

// queries
import { GET_ICON_DETAIL_PAGE_DATA } from "icons/queries";

// helpers
import { getTagsData } from "core/TagToggle/helpers";
import { getMoreFromThisIconCollection, getFormattedDate } from "./helpers";

import { IconEditorProvider } from "js/contexts/IconEditorContext";

import { BROWSE_ICONS_BY_TAG } from "data/links";

const IconDetailPage = ({ match }) => {
    const { slugWithId } = match.params;

    const iconId = slugWithId.split("-").pop();

    // always start scroll position at the top
    useEffect(() => {
        window.history.scrollRestoration = "manual";
        window.scrollTo(0, 0);
    }, []);

    const { loading, data } = useQuery(GET_ICON_DETAIL_PAGE_DATA, {
        fetchPolicy: "cache-and-network",
        skip: !iconId,
        variables: {
            iconId
        }
    });

    /* If react-router changes the icon page URL, `data` will reference the previous icon.
     * `idChanged` checks if the `data` is still relevant. `useQuery` will automatically
     * update `data` when the URL changes, but it's not immediate (we're waiting on the network).
     */
    const idChanged =
        data &&
        data.iconDetail &&
        String(data.iconDetail.id) !== String(iconId);

    // Only show loading if we're still waiting on the data.
    if (loading && (!data || idChanged)) {
        return <Loading />;
    }

    const isNotFound =
        (!data || !data.iconDetail || data.iconDetail === null) && !loading;

    if (isNotFound) return <NotFound />;

    const { iconDetail, moreLikeThisIcon, currentUser } = data || {};
    const { isSuperuser = false } = currentUser || {};

    const moreLikeThis = getMoreFromThisIconCollection({ moreLikeThisIcon });

    const tags = getTagsData({
        tags: iconDetail.tags,
        tagPath: BROWSE_ICONS_BY_TAG,
        tagPathReplace: ":term",
        type: "icons"
    });

    const formattedCreatedAt = getFormattedDate({ iconDetail });

    // NOTE:
    // Some of these values also exist in templates/frontend/icon_detail.html
    const structuredDataForSEO = {
        "@context": "http://schema.org",
        "@type": "ImageObject",
        additionalType: "icon",
        indexContent: "index,follow,max-image-preview:large",
        name: iconDetail.title,
        author: iconDetail.creator.name,
        contentUrl: iconDetail.thumbnails.thumbnail200,
        width: "200",
        height: "200",
        description: `Check out ${iconDetail.title} icon created by ${iconDetail.creator.name}`,
        keywords: iconDetail.tags.map(tag => tag.name),
        publisher: "The Noun Project",
        isAccessibleForFree: true,
        ...(iconDetail.createdAt
            ? { datePublished: new Date(iconDetail.createdAt).toISOString() }
            : {})
    };

    return (
        <>
            <IconEditorProvider iconId={iconDetail.id}>
                {iconDetail && (
                    <PageMeta
                        title={`${iconDetail.title} Icon - Free PNG & SVG ${iconDetail.id} - Noun Project`}
                        useSocialMediaTags
                        previewImg={iconDetail.thumbnails.thumbnail200}
                        previewImgAlt={`${iconDetail.title} icon`}
                        twitterDescription={`Love this icon by ${iconDetail.creator.name} from @NounProject`}
                        description={`Free vector & PNG ${iconDetail.title} image ${iconDetail.id} by ${iconDetail.creator.name}. Get this royalty free image & millions more free icons from the world's most diverse collection.`}
                        indexContent="index,follow,max-image-preview:large"
                        structuredData={structuredDataForSEO}
                    />
                )}

                <IconDetailLayout
                    heroRightColumnContent={
                        <IconDetailSteps icon={iconDetail} />
                    }
                    icon={iconDetail}
                    moreLikeThisIcon={moreLikeThis}
                    tags={tags}
                    createdAt={formattedCreatedAt}
                    isSuperuser={isSuperuser}
                />
            </IconEditorProvider>

            {/* script to ensure focus visible with keyboard not with mouse: */}
            <script src={focusVisible} async />
        </>
    );
};

export default IconDetailPage;
